import React, { useRef, useState, useEffect, useCallback } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { randomNumber } from '_ui-core-js/utilities/randomNumber.js';
import { parallax } from '_ui-core-js/libraries/parallax.js';
import { BackgroundMedia } from '_ui-core-js/React/components/BackgroundMedia.jsx';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { Picture } from '_ui-core-js/React/components/Picture.jsx';
import { LoadingCharacters } from '_ui-core-js/React/components/LoadingCharacters.jsx';
import { MEDIA_TYPES } from '_ui-core-js/constants/config.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js//constants/analytics.js';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities/fireGTMTrackingEvent.js';
import { VIEWS } from './StyleSelector.jsx';
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';

const parallaxImageProps = [
  {
    key: 'parallax-image-1',
    gridClasses: 'col-2 col-lg-1 offset-neg-2',
    parallaxSpeed: -1,
    parallaxContraint: 200,
    top: '15%'
  },
  {
    key: 'parallax-image-2',
    gridClasses: 'col-5 col-lg-3 offset-neg-1 ',
    parallaxSpeed: -5,
    parallaxContraint: 200,
    top: '45%'
  },
  {
    key: 'parallax-image-3',
    gridClasses: 'col-4 col-lg-2',
    parallaxSpeed: -1,
    parallaxContraint: 200,
    top: 0
  },
  {
    key: 'parallax-image-4',
    gridClasses: 'col-5 col-lg-3 offset-4 offset-lg-3',
    parallaxSpeed: -2,
    parallaxContraint: 200,
    top: '75%'
  },
  {
    key: 'parallax-image-5',
    gridClasses: 'col-2 col-lg-1 offset-4',
    parallaxSpeed: -9,
    parallaxContraint: 200,
    top: '25%',
    zIndex: 1
  },
  {
    key: 'parallax-image-6',
    gridClasses: 'col-3 col-lg-2 offset-5 offset-lg-4',
    parallaxSpeed: -4,
    parallaxContraint: 200,
    top: '20%'
  },
  {
    key: 'parallax-image-7',
    gridClasses: 'col-3 col-lg-2 offset-9 offset-lg-7',
    parallaxSpeed: -5,
    parallaxContraint: 200,
    top: '60%'
  },
  {
    key: 'parallax-image-8',
    gridClasses: 'col-2 col-lg-1 offset-8',
    parallaxSpeed: -5,
    parallaxContraint: 200,
    top: '20%'
  },
  {
    key: 'parallax-image-9',
    gridClasses: 'col-4 col-lg-3 offset-11 offset-lg-9',
    parallaxSpeed: -2,
    parallaxContraint: 200,
    top: '10%'
  },
  {
    key: 'parallax-image-10',
    gridClasses: 'col-4 col-lg-2 offset-13 offset-lg-12',
    parallaxSpeed: -4,
    parallaxContraint: 200,
    top: '20%'
  },
  {
    key: 'parallax-image-11',
    gridClasses: 'col-3 col-lg-1 offset-14 offset-lg-12',
    parallaxSpeed: -7,
    parallaxContraint: 200,
    top: '90%'
  }
];

export function LoadingScreen(props) {

  const { block, state, rootElRef, data, user } = props;
  const [viewTransitionOut, setViewTransitionOut] = useState(false);
  const parallaxRef = useRef();

  useEffect(() => {
    setTimeout(() => {
      parallaxRef.current = parallax({
        rootElement: rootElRef.current,
        selector: '[data-parallax-target]',
        trigger: 'controls',
        autoScrollSpeed: 5
      });
      parallaxRef.current.init();
      parallaxRef.current.controls.start();
    }, 100);
    return () => {
      parallaxRef.current?.destroy();
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setViewTransitionOut(true);
    }, randomNumber(5,8) * 1000);
  }, []);

  useEffect(() => {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.workflowEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxStyleSelector, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.workflowStep,
      label: CORE_GTM_TRACKING_VALUES.label.prepareResult
    }, {
      workflow: {
        name: LBX_GTM_TRACKING_VALUES.componentName.lbxStyleSelector,
        step: CORE_GTM_TRACKING_VALUES.label.prepareResult,
        conversion: 0,
        variant: user.userPreferences && user.userPreferences.personality ? user.userPreferences.personality : CORE_GTM_TRACKING_VALUES.label.general
      }
    });
  }, []);

  const parallaxImage = useCallback((image, props) => (
    !image.base ? 
    null :
    <div 
    key={props.key}
    className={`${block}__image ${props.gridClasses} parallax-active`}
    style={{ top: props.top, zIndex : props.zIndex || 0 }}
    data-parallax-target={true} 
    data-parallax-speed={props.parallaxSpeed}
    data-parallax-constraint={1000}
    >
      <Picture base={image.base} alt={image.alt} />
    </div>
  ), []);

  return (
    <CssAnimation 
    exit={{ name: 'fade-out', duration: 0.75 }} 
    triggerExit={viewTransitionOut}
    onExitEnd={() => state.setActiveView(VIEWS.recommendations.name)}
    >
      <div className={classNames({
        [`${block}__screen-container`]: true,
        [`${block}__screen-container--${state.activeView}`]: true,
        'dark-theme': true
      })}>
        <CssAnimation 
        enter={{ name: 'fade-in', duration: 2 }} 
        triggerEnter={true} 
        renderView={!!data.backgroundVideoUrl}
        >
          <div>
            <BackgroundMedia 
            mediaType={MEDIA_TYPES.video} 
            mediaUrl={data.backgroundVideoUrl} 
            videoConfig={{ muted: state.audioMuted }} 
            />
          </div>
        </CssAnimation>
        <CssAnimation 
        enter={{ name: 'fade-in', duration: 1 }} 
        triggerEnter={true} 
        renderView={!!data.images}
        >
          <div className={`${block}__images-container container-xxxl`}>
            { 
              data.images?.map((image, index) => 
                parallaxImageProps[index] ? parallaxImage(image, parallaxImageProps[index]) : null)
            }
          </div>
        </CssAnimation>
        <div className={classNames({
          [`${block}__content-container`]: true,
          [`${block}__content-container--${state.activeView}`]: true,
          'utils__section-padding-small--uniform': true,
          'container-xxxl': true
         })}>
          <h2 className={`${block}__title`}>
            { data.title }     
            <LoadingCharacters classes='h2'/>  
          </h2>
        </div>
      </div>
    </CssAnimation>
  )
};

LoadingScreen.propTypes = {
  block: propTypes.string.isRequired,
  state: propTypes.object.isRequired,
  rootElRef: propTypes.object.isRequired,
  data: propTypes.object.isRequired
};
